import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/axiosInterceptor';

/*
const authContext = createContext();
const AuthProvider = ({ children }) => {
    console.log("AUTHENTICATION")
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const storedAuth = sessionStorage.getItem('isAuthenticated')
        return storedAuth ? JSON.parse(storedAuth) : false
    });
    const [usrGblRole, setUsrGblRole] = useState(false)
    const [userRole, setUserRole] = useState(() => {
        const storedRole = sessionStorage.getItem('role')
        return storedRole ? JSON.parse(storedRole) : false
    })

    const changeAuth = (auth) => {
        setIsAuthenticated(auth);
    };
    const changeUsrRole = (role) => {
        setUsrGblRole(role);
    };
    const updateUserRole = (type) => {
        setUserRole(type);
    };

    return (
        <authContext.Provider value={{ isAuthenticated, changeAuth, usrGblRole, changeUsrRole, updateUserRole, userRole }}>
            {children}
        </authContext.Provider>
    );
};

export const useAccount = () => useContext(authContext)
export { AuthProvider, authContext };
*/

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const hasShownExpiredAlert = useRef(false);
    const handleAuthFailure = useCallback(() => {
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const handleSessionExpired = useCallback((e) => {
        if (hasShownExpiredAlert.current) return;
        hasShownExpiredAlert.current = true;
        handleAuthFailure();
        if (e.type === "auth:expired") { alert("Your session has expired. Please log in again."); }

        setTimeout(() => {
            navigate('/login');
            hasShownExpiredAlert.current = false;
        }, 100);
    }, [handleAuthFailure, navigate]);

    // Listen for auth expiration from interceptor
    useEffect(() => {
        window.addEventListener('auth:expired', handleSessionExpired);
        window.addEventListener('auth:expired-token', handleSessionExpired);
        return () => {
            window.removeEventListener('auth:expired', handleSessionExpired);
            window.removeEventListener('auth:expired-token', handleSessionExpired);
        }
    }, [handleSessionExpired]);

    const checkAuth = useCallback(async () => {
        try {
            const response = await api.get('/api/userauth/analytics');
            if (response.data.auth) {
                setUser({
                    name: response.data.usr,
                    id: response.data.id,
                    role: response.data.role,
                });
                setIsAuthenticated(true);
                // setRetailChannels(response?.data?.retailChannels)
            } else {
                handleAuthFailure();
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            handleAuthFailure();
        } finally {
            setIsLoading(false);
        }
    }, [handleAuthFailure]);

    // Login function that accepts your backend's response format
    const login = useCallback(async (user, role) => {
        try {
            setUser({
                name: user,
                // id: userResponse.id,
                role: role,
            });
            // setRetailChannels(userResponse?.retailChannels)
            setIsAuthenticated(true);
            hasShownExpiredAlert.current = false;

            return { success: true };
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, error: 'Failed to set auth state' };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.get('/api/logout/analytics');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            handleAuthFailure();
            setIsAuthenticated(false)
            hasShownExpiredAlert.current = false;
            navigate('/login');
        }
    }, [handleAuthFailure, navigate]);

    // Check auth on mount
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);
    const value = {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkAuth,
        // Legacy compatibility - if you have code using these
        userRole: user?.role,
        userName: user?.name,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Export AuthContext for backward compatibility
export const authContext = AuthContext;